/**
 * Email "sync now" control.
 *
 * Presentational only — the sync action, in-flight flag and last-synced
 * timestamp all live in Layout (single source of truth, single
 * auto-sync-on-reopen effect). This renders in two places:
 *   - variant="bar"     compact icon button in the mobile top bar
 *   - variant="sidebar" full-width labelled row in the desktop sidebar
 *
 * Email is pulled server-side every ~5 min regardless; this just lets
 * the user force an immediate pull + view refresh, and shows how fresh
 * the data is so the "is this current?" question answers itself.
 */
import { IconRefresh } from './Icons';

/** "just now" / "3m ago" / "2h ago" from a ms timestamp. */
export function freshnessLabel(lastSyncedAt: number | null, now: number): string {
  if (!lastSyncedAt) return 'Sync email';
  const mins = Math.floor((now - lastSyncedAt) / 60000);
  if (mins <= 0) return 'Synced just now';
  if (mins < 60) return `Synced ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `Synced ${hrs}h ago`;
}

export function EmailSyncButton({
  variant,
  syncing,
  lastSyncedAt,
  now,
  onSync,
}: {
  variant: 'bar' | 'sidebar';
  syncing: boolean;
  lastSyncedAt: number | null;
  now: number;
  onSync: () => void;
}) {
  const label = syncing ? 'Syncing…' : freshnessLabel(lastSyncedAt, now);
  const spin = syncing ? ' animate-spin' : '';

  if (variant === 'bar') {
    // Compact: icon only (with a spinning state). The freshness reads
    // out via aria-label + title so it stays a single tap target on a
    // phone without eating top-bar width.
    return (
      <button
        type="button"
        onClick={onSync}
        disabled={syncing}
        aria-label={label}
        title={label}
        className="relative p-2 text-kg-grey-700 hover:text-kg-black dark:text-kg-grey-300 dark:hover:text-kg-grey-50 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
      >
        <IconRefresh className={`w-5 h-5${spin}`} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onSync}
      disabled={syncing}
      title="Pull the latest email now and refresh"
      className="w-full flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-widest text-kg-grey-700 hover:text-kg-black hover:bg-kg-grey-50 dark:text-kg-grey-300 dark:hover:text-white dark:hover:bg-kg-grey-800 transition-colors rounded-sm disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black"
    >
      <IconRefresh className={`w-5 h-5 shrink-0${spin}`} />
      <span className="flex-1 text-left normal-case tracking-normal text-sm text-kg-grey-600 dark:text-kg-grey-400">
        {label}
      </span>
    </button>
  );
}
